'use client'

import type { IconDefinition } from '@fortawesome/fontawesome-svg-core'
import { faSearch } from '@fortawesome/pro-regular-svg-icons'
import { styled } from 'styled-components'

import { EmptyState, FormattedMessage } from '@b-stock/fe-core/bstock-react'

import ListingCard from '@/components/listings/listingCard/ListingCard'
import ListingCardSkeleton from '@/components/listings/listingCard/ListingCardSkeleton'
import type { ScreenName } from '@/helpers/telemetry/ScreenName'
import type { SearchResult } from '@/queries/listingSearchQuery'

export const GridMinMax = `minmax(14.25rem, 1fr)`
export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, ${GridMinMax});
  grid-auto-rows: max-content;
  max-width: 100%;
  gap: 1.5rem;
  contain: layout;
  overflow: auto;
`

// Tighter padding + smaller icon so the similar inventory section sits higher on the no-results page
const CompactEmptyState = styled(EmptyState)`
  && {
    padding-top: 2rem;
    padding-bottom: 1rem;
  }
`

const ContentWrapper = styled.div<{ $isLoading: boolean }>`
  opacity: ${({ $isLoading }) => ($isLoading ? 0.9 : 1)};
  transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  will-change: opacity;
  backface-visibility: hidden;
  transform: translateZ(0);
  contain: layout style paint;
  overflow: visible;
  padding: 1.25rem; // Adjust as needed based on shadow spread
  margin: -1.25rem;
`

export type ListingGridEmptyState = {
  icon: IconDefinition
  title: React.ReactNode
  description?: React.ReactNode
  primaryButton?: { label: React.ReactNode; handleClick: () => void }
}

const ListingGrid = ({
  emptyState,
  error,
  isLoading,
  listings,
  noListings,
  screenName,
}: {
  emptyState?: ListingGridEmptyState
  error?: Error | null
  isLoading: boolean
  listings: SearchResult[]
  noListings: boolean
  screenName: ScreenName
}) => {
  return (
    <>
      {isLoading && listings.length === 0 ? (
        <Grid data-testid="listing-grid">
          {Array.from({ length: 24 }, (_, k) => k).map((k) => (
            <ListingCardSkeleton key={k} />
          ))}
        </Grid>
      ) : error ? (
        <div>Error: {error.message}</div>
      ) : noListings ? (
        <CompactEmptyState
          icon={emptyState?.icon ?? faSearch}
          size={5}
          title={
            emptyState?.title ?? <FormattedMessage id="Listings.noResults" />
          }
          description={emptyState?.description}
          primaryButton={emptyState?.primaryButton}
        />
      ) : (
        <ContentWrapper $isLoading={isLoading}>
          <Grid data-testid="listing-grid">
            {listings.map((listing, index) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                screenName={screenName}
                position={index + 1}
              />
            ))}
          </Grid>
        </ContentWrapper>
      )}
    </>
  )
}

export default ListingGrid
